import React from 'react';
import { PaginationIndicators } from '../pagination-indicators/PaginationIndicators';
import styles from './OrderColumn.module.css';
import { OrderCard } from '../order-card/OrderCard';
import type { GridDimensions } from '@/hooks/useResponsiveGrid';
import { OrbOrderStatus } from '@/types/orderTypes';
import type { Order } from '@/types/orderTypes';

export interface OrderColumnProps {
  title: string;
  status: OrbOrderStatus;
  orders: Order[];
  gridDimensions: GridDimensions;
  currentPage?: number;
  totalPage?: number;
}

export const OrderColumn: React.FC<OrderColumnProps> = ({
  title,
  status,
  orders,
  gridDimensions,
  currentPage = 1,
  totalPage = 1,
}) => {
  const gridStyle: React.CSSProperties = {
    gridTemplateColumns: `repeat(${gridDimensions.columns}, 1fr)`,
    gridTemplateRows: `repeat(${gridDimensions.rows}, 1fr)`,
    gap: '8px',
    width: '100%',
    height: '100%',
  };

  // Get empty state message based on status
  const getEmptyMessage = () => {
    if (status === OrbOrderStatus.PREPARING) {
      return 'No orders being prepared';
    }
    return 'No orders are ready';
  };

  return (
    <div className={[styles.section, styles[status]].filter(Boolean).join(' ')}>
      <div className={styles.sectionHeader}>{title}</div>
      <div className={styles.gridContainer}>
        {orders.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyMessage}>{getEmptyMessage()}</div>
          </div>
        ) : (
          <div
            data-testid={`order-column-${String(status).toLowerCase()}`}
            className={styles.grid}
            style={gridStyle}
          >
            {orders.map((order, index) => {
              // First row in ready column gets special styling
              const isFirstRow = index < gridDimensions.columns;
              const isReady = status === OrbOrderStatus.READY;

              return (
                <OrderCard
                  key={order.id}
                  order={order}
                  isFirstRowReady={isReady && isFirstRow}
                />
              );
            })}
          </div>
        )}
      </div>
      {currentPage && totalPage && orders.length > 0 && (
        <PaginationIndicators currentPage={currentPage} totalPage={totalPage} />
      )}
    </div>
  );
};
